import type { SessionEvent } from '@/schemas';

type SessionEventId = SessionEvent['id'];

type UndoGraphEntry = {
  id: SessionEventId;
  kind: 'undo' | 'redo';
};

/**
 * Pre-compute activity state for each event, taking undo/redo chains into account.
 * The resolver ensures an event is considered inactive when it has more active undo operations
 * than redo operations in its reference chain (e.g. undoing an undo re-activates the target event).
 */
export const createSessionEventActivityResolver = (events: SessionEvent[]) => {
  const eventsById = new Map<SessionEventId, SessionEvent>();
  const references = new Map<SessionEventId, UndoGraphEntry[]>();

  for (const event of events) {
    eventsById.set(event.id, event);

    if (event.type === 'EventUndone') {
      const list = references.get(event.payload.undoneEventId) ?? [];
      list.push({ id: event.id, kind: 'undo' });
      references.set(event.payload.undoneEventId, list);
    } else if (event.type === 'EventRedone') {
      const list = references.get(event.payload.redoneEventId) ?? [];
      list.push({ id: event.id, kind: 'redo' });
      references.set(event.payload.redoneEventId, list);
    }
  }

  const cache = new Map<SessionEventId, boolean>();
  const resolving = new Set<SessionEventId>();

  const evaluate = (eventId: SessionEventId): boolean => {
    if (cache.has(eventId)) {
      return cache.get(eventId)!;
    }

    if (resolving.has(eventId)) {
      throw new Error(`Circular undo/redo chain detected for event ${eventId}`);
    }
    resolving.add(eventId);

    const refs = references.get(eventId) ?? [];
    let balance = 0;

    for (const ref of refs) {
      const referencedEvent = eventsById.get(ref.id);
      if (!referencedEvent) {
        continue;
      }

      if (!evaluate(ref.id)) {
        continue;
      }

      if (ref.kind === 'undo') {
        balance += 1;
      } else {
        balance = Math.max(0, balance - 1);
      }
    }

    const isActive = balance === 0;
    cache.set(eventId, isActive);
    resolving.delete(eventId);

    return isActive;
  };

  return {
    isActive: (eventId: SessionEventId): boolean => evaluate(eventId),
    getEvent: (eventId: SessionEventId): SessionEvent | undefined => eventsById.get(eventId),
    getReferences: () => references, // Expose references map for reuse
  };
};

/**
 * Event types that should be filtered out when a session is restarted
 */
const ROUND_EVENT_TYPES = [
  'RoundStarted',
  'RoundCompleted',
  'RoundDeleted',
  'RoundNoteAdded',
  'RoundNoteRemoved',
  'RoundTagAdded',
  'RoundTagRemoved',
] as const;

const SCORE_EVENT_TYPES = ['ScoreChanged', 'ScoreSet'] as const;

const RESTART_FILTERED_EVENT_TYPES = [...ROUND_EVENT_TYPES, ...SCORE_EVENT_TYPES] as const;

type RestartFilteredEventType = (typeof RESTART_FILTERED_EVENT_TYPES)[number];

const isRestartFilteredEventType = (
  eventType: SessionEvent['type'],
): eventType is RestartFilteredEventType => {
  return RESTART_FILTERED_EVENT_TYPES.includes(eventType as RestartFilteredEventType);
};

export const getReplayableSessionEvents = (events: SessionEvent[]) => {
  const resolver = createSessionEventActivityResolver(events);

  // Find the most recent active SessionRestarted event
  const restartEvents = events.filter(
    (event) => event.type === 'SessionRestarted' && resolver.isActive(event.id),
  );

  let mostRecentRestart: SessionEvent | undefined;
  if (restartEvents.length > 0) {
    let latestTimestamp = 0;
    for (const event of restartEvents) {
      if (event.timestamp > latestTimestamp) {
        latestTimestamp = event.timestamp;
        mostRecentRestart = event;
      }
    }
  }

  // Filter events: exclude EventUndone/EventRedone, inactive events, and round/score events before restart
  return {
    resolver,
    events: events.filter((event) => {
      // Always exclude EventUndone and EventRedone
      if (event.type === 'EventUndone' || event.type === 'EventRedone') {
        return false;
      }

      // Exclude inactive events
      if (!resolver.isActive(event.id)) {
        return false;
      }

      // If there's a restart event, filter out round/score events that occurred before it
      if (mostRecentRestart && isRestartFilteredEventType(event.type)) {
        return event.timestamp >= mostRecentRestart.timestamp;
      }

      return true;
    }),
  };
};
